<i18n>
{
  "en": {
    "hello": " Verification System",
    "messages": {
      "loginSuccess": "Administrator login was successful.",
      "attentionTitle": "Notes.",
      "rules": "From here on, the screen is exactly the same as the one that appears when the applicant signs in.<br />You can also operate the system in the same way as applicants.<br />This is just a function to check the screen displayed to the current winner.<br /><strong>Please be careful not to operate it by mistake.</strong>",
      "attentionMessage": "Please make sure you understand the above precautions before moving to the user screen.",
      "agreement": "I understand the above precautions.",
      "gotoButton": "Transition to the user screen after understanding it.",
      "loginErrorMessage": "Failed to login as administrator. Please close the screen and try again from the beginning.",
      "nowLogin": "You are currently logged in as an administrator....."
    },
    "maintenance": {
      "inMaintenance": "Currently under system maintenance. <br /> Sorry for your inconvenience, but please access again after the maintenance is completed.",
      "maintenance": "Due to system maintenance, the service will be suspended for the following period.",
      "stopTerm": "[Stop period]",
      "notice": "*Depending on the maintenance work situation, it may be extended."
    }
  },
  "ja": {
    "hello": "手続きシステムへようこそ！",
    "messages": {
      "loginSuccess": "管理者ログインが成功しました。",
      "attentionTitle": "注意事項",
      "rules": "ここから先は応募者がサインインして表示される画面と全く同じです。<br />応募者と同様の操作も可能となっております。<br />あくまで現在の受賞者に表示されている画面を確認する機能ですので、<br /><strong>誤って操作をしないよう</strong>にご注意ください。",
      "attentionMessage": "上記注意事項を理解した上で、ユーザ画面に遷移してください。",
      "agreement": "上記注意事項を理解しました。",
      "gotoButton": "理解した上でユーザ画面に遷移する",
      "loginErrorMessage": "管理者ログインに失敗しました。画面を閉じて最初から操作してください。",
      "nowLogin": "現在管理者ログインを行っています....."
    },
    "maintenance": {
      "inMaintenance": "現在、システムメンテナンス中です。<br />お手数ですが、メンテナンス終了後に再度アクセスしてください。",
      "maintenance": "システムメンテナンスのため、下記期間サービスを停止致します。",
      "stopTerm": "【停止期間】",
      "notice": "※メンテナンス作業の状況によっては、延長させて頂く場合がございます。"
    }
  }
}
</i18n>

<template>
  <div>
    <Toolbar :title="campaign.name + $t('hello')" />
    <v-container>
      <v-flex
        style="justify-content: center;
          text-align: center;
          margin-top: 16px;
          margin-bottom: 16px;"
      >
        <img src="@/assets/title.png" />
      </v-flex>

      <v-layout
        row
        wrap
        align-center
        justify-center
        pt-1
        v-if="isMaintenanceSchedule"
      >
        <div class="maintenance">
          <span v-if="isMaintenance">
            <strong v-html="$t('maintenance.inMaintenance')"></strong>
          </span>
          <span v-else>
            <strong>{{ $t('maintenance.maintenance') }}</strong>
          </span>
          <br />
          {{ $t('maintenance.stopTerm') }}{{ maintenanceTerm }}<br />
          <small>({{ $t('maintenance.notice') }})</small>
        </div>
      </v-layout>

      <v-layout row wrap align-center justify-center pt-5 v-if="!isMaintenance">
        <v-flex text-xs-center xs12 sm12 md8 lg8>
          <span v-if="token">
            <template>
              <div>
                <v-alert outlined text type="success" value="true">
                  {{ $t('messages.loginSuccess') }}
                </v-alert>
              </div>
            </template>
            <br />
            <h3>{{ $t('messages.attentionTitle') }}</h3>
            <p v-html="$t('messages.rules')"></p>
            <br />
            <br />
            <br />
            <span class="red--text">
              {{ $t('messages.attentionMessage') }}
            </span>
            <br />
            <v-checkbox
              center
              v-model="doui"
              :label="$t('messages.agreement')"
            ></v-checkbox>
            <v-btn @click="temporaryTokenSignin()" :disabled="!doui">
              {{ $t('messages.gotoButton') }}
            </v-btn>
          </span>
          <span v-else>
            {{ $t('messages.nowLogin') }}
            <br />
            <br />
            <v-layout justify-center>
              <v-progress-circular
                indeterminate
                :size="50"
              ></v-progress-circular>
            </v-layout>
          </span>
        </v-flex>
        <v-flex xs12 sm12 md12> </v-flex>
      </v-layout>
    </v-container>
  </div>
</template>

<script>
import Toolbar from '@/components/molecules/Toolbar.vue';
import { mapGetters, mapActions } from 'vuex';
import { Auth } from '@/api';
import TokenManager from '@/utils/TokenManager';
import StorageManager from '@/utils/StorageManager';
import MaintenanceUtil from '@/utils/MaintenanceUtil';

export default {
  components: {
    Toolbar
  },
  data() {
    return {
      campaignCode: this.$route.params.campaignCode,
      temporaryToken: null,
      token: null,
      doui: false,
      signinError: false,
      isMaintenance: MaintenanceUtil.isMaintenance(),
      isMaintenanceSchedule: MaintenanceUtil.isMaintenanceSchedule(),
      maintenanceTerm: MaintenanceUtil.maintenanceTerm()
    };
  },
  computed: {
    ...mapGetters({ campaign: 'getCurrentCampaign' })
  },
  created() {
    StorageManager.setItem('CAMPAIGN_CODE_SESSION_KEY', this.campaignCode);
    Auth.prepare({ campaignHash: this.campaignCode })
      .then(result => {
        this.setCurrentCampaign({
          ...result.data.campaign,
          code: this.campaignCode
        });
        this.temporaryToken = result.result.auth.token;
      })
      .catch(apiError => {
        // この画面でのエラーは意図的にURLを変更されたものなので全部404
        this.temporaryToken = apiError.data.result.auth.token;
        if (
          apiError.http.statusCode === 400 ||
          apiError.http.statusCode === 401 ||
          apiError.http.statusCode === 403 ||
          apiError.http.statusCode === 404
        ) {
          this.$router.push('/404');
        }
      });
    Auth.temporaryTokenSignin({
      temporaryToken: this.$route.query.temporary_token
    })
      .then(result => {
        // トークンをセット
        this.token = result.result.auth.token;
        TokenManager.set(result.result.auth.token);
      })
      .catch(() => {
        // エラーダイアログを表示(通常失敗することはない)
        alert(this.$t('messages.loginErrorMessage'));
      });
    // メンテナンスが予定されている場合は、メンテナンスフラグを常に更新する
    if (this.isMaintenanceSchedule) {
      setInterval(
        function() {
          this.isMaintenance = MaintenanceUtil.isMaintenance();
          this.isMaintenanceSchedule = MaintenanceUtil.isMaintenanceSchedule();
        }.bind(this),
        1000
      );
    }
  },
  methods: {
    ...mapActions(['prepareToSignin', 'login', 'setCurrentCampaign']),
    temporaryTokenSignin() {
      // マイページに遷移
      this.$router.push(`/cp/${this.campaignCode}/me`);
    }
  }
};
</script>

<style scoped lang="stylus">
.maintenance {
  width: 50%;
  z-index: 99999;
  background-color: rgba(0,0,0,0);
  text-align: center;
  padding: 8px;
  border: 1px solid #ff0000;
}
</style>
